import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Layers, MessagesSquare, Box, Ruler } from "lucide-react";
import { api } from "@/lib/api";
import { FloorPlan2D } from "@/components/FloorPlan2D";
import { Logo } from "@/components/AppHeader";
import { floorView } from "@/lib/walls";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  { icon: Ruler, t: "Dimensioned 2D plans", d: "Walls, doors with swings, windows, room tags and dimension strings — drawn to scale in feet." },
  { icon: Box, t: "3D massing", d: "Every draft extrudes into a rotatable model with real openings, sills and headers." },
  { icon: MessagesSquare, t: "Revise by chat", d: "\"Push the primary suite to 16 ft\" — the plan, schedule and model update together." },
  { icon: Layers, t: "Straight into Rhino", d: "Download a layered .3dm or a rhinoscriptsyntax script that rebuilds the model in Rhino." },
];

const Nav = () => {
  const { user } = useAuth();
  return (
    <nav className="glass fixed top-0 inset-x-0 z-40 border-b border-[var(--line)] h-16 px-6 lg:px-12 flex items-center justify-between">
      <Logo />
      <div className="flex items-center gap-6">
        <a href="#features" className="hidden sm:block text-sm text-[var(--muted)] hover:text-white transition-colors" data-testid="nav-features-link">Features</a>
        <a href="#rhino" className="hidden sm:block text-sm text-[var(--muted)] hover:text-white transition-colors" data-testid="nav-rhino-link">Rhino workflow</a>
        <Link to={user ? "/dashboard" : "/login"} data-testid="nav-cta-btn" className="text-sm font-semibold px-4 py-2 bg-[var(--cyan)] text-[var(--bg)] hover:brightness-110 transition-[filter]">
          {user ? "Open studio" : "Sign in"}
        </Link>
      </div>
    </nav>
  );
};

export default function Landing() {
  const [plan, setPlan] = useState(null);
  const { user } = useAuth();
  useEffect(() => { api.get("/sample").then(({ data }) => setPlan(data)); }, []);
  const cta = user ? "/dashboard" : "/login";

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="landing-page">
      <Nav />
      <section className="relative pt-16 blueprint-grid noise overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-[1fr_1.15fr] gap-12 lg:gap-16 items-center min-h-[calc(100vh-4rem)] py-16">
          <div>
            <p className="rise font-mono text-xs uppercase tracking-[0.3em] text-[var(--cyan)] mb-6">AI schematic design · Rhino export</p>
            <h1 className="rise d1 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">
              Type the brief.<br />Get the <span className="text-[var(--cyan)]">drawing set</span>.
            </h1>
            <p className="rise d2 mt-8 text-base md:text-lg text-[var(--muted)] max-w-lg leading-relaxed">
              Tell RhinoDraft “I need a 1500 sq ft home, 3 bed, 2 bath” and it drafts a dimensioned floor plan, a 3D massing model and a Rhino file — in under a minute.
            </p>
            <div className="rise d3 mt-10 flex flex-wrap items-center gap-4">
              <Link to={cta} data-testid="hero-start-btn" className="group inline-flex items-center gap-3 px-6 py-3.5 bg-[var(--cyan)] text-[var(--bg)] font-semibold font-display hover:brightness-110 transition-[filter]">
                Start drafting <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a href="#rhino" className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] hover:text-white transition-colors" data-testid="hero-rhino-link">How the Rhino export works →</a>
            </div>
            <div className="rise d4 mt-14 grid grid-cols-3 max-w-md border-t border-[var(--line)] pt-6 font-mono">
              {[["~45s", "per draft"], [".3dm", "+ .py script"], ["1:1", "feet, to scale"]].map(([a, b]) => (
                <div key={a}><div className="text-xl text-white">{a}</div><div className="text-[10px] uppercase tracking-widest text-[var(--muted)] mt-1">{b}</div></div>
              ))}
            </div>
          </div>
          <div className="rise d2 relative">
            <div className="absolute -inset-px border border-[var(--cyan)]/30" />
            <div className="relative bg-[var(--bg)]/80 border border-[var(--line)] p-4 sm:p-6">
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mb-3">
                <span>Sheet A-101 · Floor plan</span><span className="text-[var(--cyan)]">{plan ? `${Math.round(plan.total_area)} SF` : "…"}</span>
              </div>
              <div className="aspect-[16/11]" data-testid="hero-floorplan">{plan && <FloorPlan2D plan={floorView(plan, 0)} animate hideDims />}</div>
              <div className="mt-3 border-t border-[var(--line)] pt-3 font-mono text-[11px] text-[var(--muted)] flex gap-2">
                <span className="text-[var(--amber)]">prompt ›</span> 1500 square foot single-storey home, 3 bed, 2 bath, open living
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 md:py-32">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--cyan)] mb-4">01 — What you get</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">One prompt. Four coordinated deliverables.</h2>
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 border-l border-t border-[var(--line)]">
          {FEATURES.map(({ icon: I, t, d }, i) => (
            <div key={t} className="group border-r border-b border-[var(--line)] p-8 hover:bg-[var(--surface)] transition-colors" data-testid={`feature-${i}`}>
              <div className="flex items-center justify-between mb-10">
                <I size={22} className="text-[var(--cyan)]" />
                <span className="font-mono text-[10px] text-[var(--muted)]">0{i + 1}</span>
              </div>
              <h3 className="text-lg font-semibold font-display">{t}</h3>
              <p className="mt-3 text-sm text-[var(--muted)] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="rhino" className="border-y border-[var(--line)] bg-[var(--surface)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 md:py-32 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--amber)] mb-4">02 — Rhino workflow</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">From browser to Rhino viewport in two clicks.</h2>
            <ol className="mt-10 space-y-6">
              {["Describe the building — size, bedrooms, style, site constraints.", "Iterate with the AI until the plan works.", "Download the .3dm (layered, in feet) or run the generated Python script inside Rhino."].map((s, i) => (
                <li key={s} className="flex gap-5"><span className="font-mono text-[var(--amber)] text-sm pt-0.5">0{i + 1}</span><span className="text-[var(--ink)]/90">{s}</span></li>
              ))}
            </ol>
          </div>
          <pre className="bg-[var(--bg)] border border-[var(--line)] p-6 text-[12px] leading-6 overflow-x-auto scroll-thin text-[var(--muted)]" data-testid="rhino-code-preview">
{`import rhinoscriptsyntax as rs
`}<span className="text-[var(--cyan)]">rs.UnitSystem</span>{`(9, False)  # feet
rs.AddLayer("Walls", (40, 40, 40))
rs.CurrentLayer("Walls")
box(0.0, 29.75, 0, 2.0, 30.25, 9.0)
box(2.0, 29.75, 0, 7.0, 30.25, 3.0)   `}<span className="text-[var(--amber)]"># sill</span>{`
box(2.0, 29.75, 7.0, 7.0, 30.25, 9.0) `}<span className="text-[var(--amber)]"># header</span>{`
rs.AddTextDot("Living Room 320 sf", (10, 22, 0.1))
rs.ZoomExtents()`}
          </pre>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 md:py-32 flex flex-col md:flex-row md:items-end justify-between gap-8">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-2xl">Your next scheme is one sentence away.</h2>
        <Link to={cta} data-testid="footer-start-btn" className="group inline-flex items-center gap-3 px-6 py-3.5 bg-[var(--cyan)] text-[var(--bg)] font-semibold font-display hover:brightness-110 transition-[filter] self-start">
          Open the studio <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
        </Link>
      </section>
      <footer className="border-t border-[var(--line)] px-6 lg:px-12 py-8 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] flex justify-between">
        <span>RhinoDraft AI</span><span>Schematic drafts — verify with a licensed professional</span>
      </footer>
    </div>
  );
}
